import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Steps,
  Upload,
  Popover,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { IcoUsers, IcoLock } from '../AppShell/icons';
import { HexColorPicker } from 'react-colorful';
import { toast } from '../Toast';
import { acceptTrainerInvite } from '../../services/trainerApi';
import { signInWithCustomToken, getIdToken } from '../../helpers/auth';
import { uploadMedia } from '../../utils/cloudinary';
// Same wordmark the AppShell sidebar uses (`brand-logo` styling).
// Keeps the trainer-setup hero visually consistent with what the
// trainer will see once they're inside the dashboard.
import NubodLogo from '../../assets/logos/nubod-black.png';
import './index.css';

const { TextArea } = Input;

// Custom Color Picker Component with Color Wheel
const ColorPicker = ({ color, onChange, label, description }) => {
  const [inputColor, setInputColor] = useState(color);
  const [isOpen, setIsOpen] = useState(false);

  const handleColorChange = (newColor) => {
    setInputColor(newColor);
    onChange(newColor);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputColor(value);
    if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
      onChange(value);
    }
  };

  const colorPickerContent = (
    <div className='color-picker-popover'>
      <div className='color-wheel-container'>
        <HexColorPicker color={inputColor} onChange={handleColorChange} />
      </div>
      <div className='custom-color-input'>
        <Input
          value={inputColor}
          onChange={handleInputChange}
          placeholder='#000000'
          prefix='#'
          style={{ marginTop: 12 }}
        />
      </div>
    </div>
  );

  return (
    <div className='color-picker-wrapper'>
      <div className='form-section'>
        <p className='label'>{label}</p>
        <p className='color-description'>{description}</p>
      </div>
      <Popover
        content={colorPickerContent}
        trigger='click'
        placement='bottom'
        overlayClassName='color-picker-popover-overlay'
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <div className='color-display' onClick={() => setIsOpen(!isOpen)}>
          <div className='color-preview' style={{ backgroundColor: color }} />
          <span className='color-value'>{color}</span>
          <span className='color-picker-icon'>🎨</span>
        </div>
      </Popover>
    </div>
  );
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const TrainerProfileSetup = ({ match, form }) => {
  // Token comes from the URL — admin emailed a /trainer-setup/:token
  // link. The accept-invite endpoint validates it and creates the
  // Trainer doc + Firebase password. If it's missing (e.g. someone
  // hits the page directly without an invite), we can't proceed.
  const token = match?.params?.token;
  // Welcome-email link is `/trainer-setup/:token?email=<invitee>`. We
  // read the email from the query string and pre-fill the form so the
  // trainer doesn't have to retype it — but it stays editable in case
  // the link was forwarded with a corrupted param or the trainer's
  // email is being changed mid-onboarding.
  const location = useLocation();
  const emailFromUrl =
    new URLSearchParams(location.search).get('email') || '';
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  // `logoUrl` is the persisted Cloudinary `secure_url`; rendered as
  // the preview and sent to the backend on submit. We don't keep the
  // raw File around since we don't re-upload.
  const [logoUrl, setLogoUrl] = useState('');
  const history = useHistory();
  const { getFieldDecorator, validateFields, setFieldsValue } = form;

  // Two-step flow:
  //   1. Business Identity — branding info (lets the trainer invest
  //      in seeing their business / colors / logo take shape before
  //      asking for credentials).
  //   2. Set Password — credentials are the last step. Matches the
  //      Stripe / Notion onboarding shape: "tell us about you, then
  //      pick a password to sign in next time."
  const steps = [
    {
      title: 'Business identity',
      // Match the rest of the app's Feather-style icon set instead of
      // antd's UserOutlined. Cluster-of-people reads as "business /
      // team / clients you'll be coaching."
      icon: <IcoUsers size={16} />,
      content: 'Branding & details',
    },
    {
      title: 'Set password',
      icon: <IcoLock size={16} />,
      content: 'Secure your account',
    },
  ];

  const defaultColors = ['#8b5cf6', '#10b981', '#f59e0b'];

  useEffect(() => {
    setFieldsValue({
      'business.name': '',
      'business.bio': '',
      'business.website': '',
      'business.phone': '',
      'business.colors': defaultColors,
      email: emailFromUrl,
    });
  }, [setFieldsValue, emailFromUrl]);

  const handleLogoUpload = async (info) => {
    // `beforeUpload={() => false}` stops antd's upload pipeline, so
    // the File arrives on `info.file` directly without going through
    // antd's status state machine. We do the actual upload here:
    // convert to base64 → signed upload to Cloudinary (filed under
    // this coach) → store the returned `secure_url` so the link
    // persists across reloads.
    const rawFile = info?.file?.originFileObj || info?.file;
    if (!(rawFile instanceof File)) {
      if (info?.file?.status === 'error') {
        toast.error('Logo upload failed. Please try again.');
      }
      return;
    }
    // No account session yet: the upload is signed with the invite itself
    // (the link's token + the email it was sent to), like accept-invite.
    const inviteEmail = String(emailFromUrl || form.getFieldValue('email') || '')
      .trim()
      .toLowerCase();
    if (!token || !inviteEmail) {
      toast.error('Open the link from your invite email to add a logo, or add it later in Settings.');
      return;
    }
    setLogoUploading(true);
    try {
      const base64 = await readFileAsDataUrl(rawFile);
      const secureUrl = await uploadMedia(base64, 'logo', {
        invite: { token, email: inviteEmail },
      });
      setLogoUrl(secureUrl);
      toast.success('Logo uploaded.');
    } catch (err) {
      console.error('Logo upload failed:', err);
      toast.error('Could not upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleColorChange = (index, color) => {
    const currentColors =
      form.getFieldValue('business.colors') || defaultColors;
    const newColors = [...currentColors];
    newColors[index] = color;
    form.setFieldsValue({ 'business.colors': newColors });
  };

  // Field names to validate per step. Validating only the current
  // step's fields keeps users from being blocked by empty fields on
  // future steps. The final step submits, at which point we hand the
  // accumulated form values to handleSubmit.
  const FIELDS_BY_STEP = [
    [
      'business.name',
      // Gate "Continue" on the slug too, so an invalid handle is caught
      // on step 0 (its decorator already validates on change) rather
      // than only at final submit.
      'slug',
      'business.bio',
      'business.website',
      'business.phone',
      'business.colors',
    ],
    ['email', 'password', 'passwordConfirm'],
  ];

  const handleNext = async () => {
    try {
      await validateFields(FIELDS_BY_STEP[currentStep]);
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Final submit: read the whole accumulated form state.
        const values = form.getFieldsValue();
        handleSubmit(values);
      }
    } catch (errorInfo) {
      console.log('Failed:', errorInfo);
      toast.error('Please fill all required fields correctly.');
    }
  };

  const handleSubmit = async (values) => {
    if (!token) {
      toast.error(
        'Setup link is missing its token. Ask your admin to resend the invite.'
      );
      return;
    }
    if (values.password !== values.passwordConfirm) {
      toast.error('Passwords do not match.');
      return;
    }
    if (logoUploading) {
      toast.error('Please wait for the logo to finish uploading.');
      return;
    }
    setLoading(true);
    try {
      // antd v3 `getFieldDecorator('business.name', ...)` stores the
      // value at the nested path `values.business.name`, not the flat
      // string key `values['business.name']`. Pull the sub-object up
      // front so we don't accidentally re-introduce the flat-access
      // bug below.
      const business = values.business || {};
      // Form state stores colors as an array (matches the picker UI),
      // but the Trainer schema expects `{primary, secondary, accent}`.
      // Convert at the boundary so Mongoose can cast cleanly into the
      // typed subdoc. Sending the bare array used to corrupt the
      // saved doc — schema cast turned each typed field into an
      // array of the same nulls.
      const rawColors = business.colors;
      const colorsForApi = Array.isArray(rawColors)
        ? {
            primary: rawColors[0],
            secondary: rawColors[1],
            accent: rawColors[2],
          }
        : rawColors;
      const payload = {
        token,
        // Server binds redemption to (token, email) — see the
        // accept-invite handler. Lowercased to match how User.email
        // is stored.
        email: String(values.email || '').trim().toLowerCase(),
        password: values.password,
        // No isIndependent field — the trainer no longer self-selects it. The
        // accept-invite endpoint defaults to independent; org invites set the
        // org membership server-side instead.
        // Optional white-label subdomain handle (top-level on the
        // Trainer doc). '' when skipped; the server validates + enforces
        // reserved words / uniqueness and surfaces any error via the
        // catch below.
        slug: values.slug || '',
        business: {
          name: business.name,
          bio: business.bio || '',
          colors: colorsForApi,
          website: business.website || '',
          phone: business.phone || '',
          // Persistent Cloudinary `secure_url` from the upload widget.
          // Empty string when the trainer skipped the logo step.
          logo: logoUrl || '',
        },
      };

      const { customToken } = await acceptTrainerInvite(payload);

      // Sign the trainer in immediately with the returned custom
      // token so they land on the studio overview authenticated. If
      // the backend couldn't mint one (rare — Firebase Admin SDK
      // issue), fall back to /login where they can use the password
      // they just chose.
      if (!customToken) {
        // Backend couldn't mint a custom token (most likely the
        // firebase-admin service-account credential is missing or
        // misconfigured on the API). The account is fully set up
        // server-side; the user just needs to sign in normally.
        console.warn(
          'accept-invite returned no customToken; falling through to /login'
        );
      } else {
        try {
          await signInWithCustomToken(customToken);
          const idToken = await getIdToken();
          localStorage.setItem('token', idToken);
          // Default trainer viewMode = 'trainer' (Studio Overview).
          // Sidebar + the rest of the app read this key to decide
          // whether to render the trainer or client UI. Setting it
          // explicitly here ensures the very first dashboard render
          // after setup picks the right side.
          try {
            localStorage.setItem('viewMode', 'trainer');
          } catch (_) {
            // localStorage may be blocked; fall through.
          }
          toast.success('Profile created — welcome!');
          // Full-page load (NOT an SPA history.push): the app starts in
          // Redux `isRegistration: true`, so AuthContext deliberately doesn't
          // set `currentUser` and the dashboard guard would bounce to /login.
          // A real reload re-runs the mount-time `loadUser()`, which reads the
          // token we just stored, authenticates, and flips isRegistration=false
          // — exactly what a manual refresh does, but automatically.
          // Land on the one-time welcome (app-download prompt), which then
          // continues to /dashboard/admin. The `?welcome=1` gate survives this
          // full reload (unlike a router location.state hand-off).
          window.location.assign('/trainer-welcome?welcome=1');
          return;
        } catch (signInErr) {
          console.error('Custom-token sign-in failed:', signInErr);
          // Fall through to login redirect.
        }
      }
      toast.success('Profile created. Please sign in with your new password.');
      history.push('/login');
    } catch (error) {
      const apiMsg =
        error?.body?.error ||
        error?.body?.msg ||
        (Array.isArray(error?.body?.errors) && error.body.errors[0]?.msg) ||
        error?.message ||
        'Failed to set up your profile. Please try again.';
      toast.error(apiMsg);
      console.error('Error accepting trainer invite:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderBusinessIdentity = () => (
    <div className='setup-card'>
      {/* ----- Basics --------------------------------------------- */}
      <div className='setup-fieldset'>
        <h4 className='setup-fieldset-title'>The basics</h4>
        <p className='setup-help'>
          How your business appears to clients across Nubod.
        </p>

        <div className='form-section'>
          <p className='label'>*Business name</p>
          {getFieldDecorator('business.name', {
            rules: [
              { required: true, message: 'Please enter your business name' },
            ],
          })(<Input placeholder='Your business name' />)}
        </div>

        <div className='form-section'>
          <p className='label'>Your Nubod address</p>
          <p className='setup-help'>
            A branded web link for your clients. You can add or change this
            later in Settings — leave it blank to use the default Nubod app.
          </p>
          {getFieldDecorator('slug', {
            // Normalize as they type: lowercase, no spaces. The server is
            // the source of truth for reserved words + uniqueness.
            getValueFromEvent: (e) =>
              e.target.value.toLowerCase().replace(/\s+/g, ''),
            rules: [
              {
                validator: (rule, value, callback) => {
                  if (!value) return callback(); // optional
                  const v = String(value);
                  if (v.length < 3 || v.length > 30) {
                    return callback('Address must be 3–30 characters');
                  }
                  if (
                    v.includes('--') ||
                    !/^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/.test(v)
                  ) {
                    return callback(
                      'Use lowercase letters, numbers, and hyphens (no leading or trailing hyphen)'
                    );
                  }
                  callback();
                },
              },
            ],
          })(
            <Input
              addonAfter='.getnubod.com'
              placeholder='yourbusiness'
              autoComplete='off'
            />
          )}
        </div>

        <div className='form-section'>
          <p className='label'>Bio</p>
          {getFieldDecorator('business.bio')(
            <TextArea
              rows={3}
              placeholder='A short description clients see in shared workouts and your profile.'
            />
          )}
        </div>

      </div>

      {/* ----- Branding ------------------------------------------- */}
      <div className='setup-fieldset'>
        <h4 className='setup-fieldset-title'>Branding</h4>
        <p className='setup-help'>
          A logo and three colors that brand the mobile app your clients
          use.
        </p>

        <div className='form-section'>
          <p className='label'>Logo</p>
          <Upload
            name='logo'
            listType='picture-card'
            className='logo-uploader'
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleLogoUpload}
            disabled={logoUploading}
          >
            {logoUrl ? (
              <img src={logoUrl} alt='logo' style={{ width: '100%' }} />
            ) : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>
                  {logoUploading ? 'Uploading…' : 'Upload Logo'}
                </div>
              </div>
            )}
          </Upload>
          <p className='setup-help'>
            PNG, JPG, or SVG, at least 400px wide. Transparent backgrounds and
            wide logos look best.
          </p>
        </div>

        <div className='form-section'>
          <p className='label'>*Brand colors</p>
          <p className='setup-help'>
            Applied live to your clients' mobile app.
          </p>
          {getFieldDecorator('business.colors', {
            // The wrapped child is a plain <div> (not a controlled antd
            // input), so it can't drive the decorator's value via
            // onChange. Provide an explicit `initialValue` so the form
            // store has the defaults from the start, and a custom
            // validator that inspects the array shape directly rather
            // than relying on antd's `required`-on-non-input semantics.
            initialValue: defaultColors,
            rules: [
              {
                validator: (rule, value, callback) => {
                  if (
                    !Array.isArray(value) ||
                    value.length < 3 ||
                    !value.slice(0, 3).every(
                      (c) => typeof c === 'string' && /^#[0-9A-Fa-f]{6}$/.test(c)
                    )
                  ) {
                    callback('Please select 3 brand colors');
                    return;
                  }
                  callback();
                },
              },
            ],
          })(
            <div className='color-picker-container'>
              {[
                {
                  index: 0,
                  label: 'Primary',
                  description: 'Buttons, active tabs, links, and progress bars.',
                },
                {
                  index: 1,
                  label: 'Secondary',
                  description: 'Pairs with primary for gradient header bands.',
                },
                {
                  index: 2,
                  label: 'Accent',
                  description: 'Small emphasis and call-to-action details.',
                },
              ].map(({ index, label, description }) => (
                <ColorPicker
                  key={index}
                  color={
                    form.getFieldValue('business.colors')?.[index] ||
                    defaultColors[index]
                  }
                  onChange={(color) => handleColorChange(index, color)}
                  label={label}
                  description={description}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ----- Optional details ----------------------------------- */}
      <div className='setup-fieldset'>
        <h4 className='setup-fieldset-title'>
          Contact <span className='setup-optional'>· optional</span>
        </h4>
        <p className='setup-help'>
          Where clients can reach you outside of Nubod.
        </p>

        <div className='setup-grid-2'>
          <div className='form-section'>
            <p className='label'>Website</p>
            {getFieldDecorator('business.website')(
              <Input placeholder='https://yourwebsite.com' />
            )}
          </div>
          <div className='form-section'>
            <p className='label'>Phone</p>
            {getFieldDecorator('business.phone')(
              <Input placeholder='+1 (555) 123-4567' />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSetPassword = () => (
    <div className='setup-card'>
      <div className='setup-fieldset'>
        <h4 className='setup-fieldset-title'>Pick your password</h4>
        <p className='setup-help'>
          Choose the password you'll use to sign in to your account.
        </p>

        <div className='form-section'>
          {/* Pre-filled from the welcome-email link's `?email=` param.
              Editable in case the link was forwarded with a corrupted
              param. The accept-invite endpoint requires (token, email)
              to match the persisted invite — see services/trainerApi.js. */}
          <p className='label'>*Email</p>
          {getFieldDecorator('email', {
            rules: [
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Please enter a valid email' },
            ],
          })(<Input placeholder='your@email.com' />)}
        </div>
        <div className='form-section'>
          <p className='label'>*Password</p>
          {getFieldDecorator('password', {
            rules: [
              { required: true, message: 'Password is required' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ],
          })(<Input.Password placeholder='At least 8 characters' />)}
        </div>
        <div className='form-section'>
          <p className='label'>*Confirm password</p>
          {getFieldDecorator('passwordConfirm', {
            rules: [
              { required: true, message: 'Please confirm your password' },
              {
                validator: (rule, value, callback) => {
                  if (value && value !== form.getFieldValue('password')) {
                    callback('Passwords do not match');
                  } else {
                    callback();
                  }
                },
              },
            ],
          })(<Input.Password placeholder='Re-enter your password' />)}
        </div>
      </div>
    </div>
  );

  // Render BOTH step contents and toggle visibility with CSS rather
  // than mounting/unmounting per step. antd v3's `getFieldDecorator`
  // drops a field's value from the form store the moment the wrapped
  // input unmounts — there's no `preserve` flag in v3. Without this,
  // navigating between steps silently clears the inputs and the
  // submit payload reaches the backend missing fields.
  //
  // Step order: Business Identity (0) → Set Password (1). Matches the
  // SaaS-onboarding convention of credentials-last.
  const renderStepContent = () => (
    <>
      <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
        {renderBusinessIdentity()}
      </div>
      <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
        {renderSetPassword()}
      </div>
    </>
  );

  return (
    <div className='trainer-profile-setup'>
      <div className='setup-shell'>
        {/* ---------- Left rail ---------------------------------------
            Brand presence + value props. On wide viewports this fills
            ~40% of the page with a gradient background; on mobile it
            collapses to a compact header above the form. Replaces the
            old form-only layout that competitors visually outclassed. */}
        <aside className='setup-rail'>
          <div className='setup-rail-inner'>
            {/* Logo sits at the top of the inner so it shares the inner's
                padding-left with the copy below (same left edge), navbar-style. */}
            <img
              src={NubodLogo}
              alt='Nubod'
              className='setup-brand-logo'
            />
            <div className='setup-rail-copy'>
              <h1 className='setup-rail-title'>
                Welcome to Nubod
              </h1>
              <p className='setup-rail-sub'>
                The coaching platform built for trainers who care
                whether their clients actually follow through.
              </p>
            </div>
            <ul className='setup-value-props'>
              <li>
                <span className='setup-value-check' aria-hidden>✓</span>
                <span>
                  <strong>Build once, assign with one tap.</strong>{' '}
                  Workouts, programs, and meal plans live in a curated
                  library you reuse across every client.
                </span>
              </li>
              <li>
                <span className='setup-value-check' aria-hidden>✓</span>
                <span>
                  <strong>See adherence at a glance.</strong>{' '}
                  Roster-wide check-in tracking so you stop chasing
                  and start coaching.
                </span>
              </li>
              <li>
                <span className='setup-value-check' aria-hidden>✓</span>
                <span>
                  <strong>Everything in one place.</strong>{' '}
                  Direct chat, broadcasts, progress photos, food logs,
                  and workout history — no tab-juggling.
                </span>
              </li>
            </ul>
          </div>
        </aside>

        {/* ---------- Right rail: the form ----------------------------
            Steps strip + active step content + actions. Same form
            machinery as before; only the surrounding chrome changed. */}
        <main className='setup-form-area'>
          <div className='setup-form-area-inner'>
            <div className='setup-form-head'>
              <p className='setup-eyebrow'>Trainer onboarding</p>
              <h2 className='setup-form-title'>
                Set up your trainer profile
              </h2>
              <p className='setup-form-sub'>
                Add your business details and branding, then pick a
                password. Takes about 2 minutes.
              </p>
            </div>

            <div className='setup-steps'>
              <Steps current={currentStep}>
                {steps.map((step, index) => (
                  <Steps.Step
                    key={index}
                    title={step.title}
                    description={step.content}
                    icon={step.icon}
                  />
                ))}
              </Steps>
            </div>

            <div className='setup-content'>
              <Form layout='vertical' className='setup-form'>
                {renderStepContent()}
              </Form>
            </div>

            <div className='setup-actions'>
              {currentStep > 0 && (
                <Button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className='back-button'
                >
                  Back
                </Button>
              )}
              <Button
                type='primary'
                onClick={handleNext}
                loading={loading}
                className='next-button'
              >
                {currentStep === steps.length - 1
                  ? 'Create Profile'
                  : 'Continue'}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Form.create()(TrainerProfileSetup);
